// src/utils/paymentUtils.ts - Utilitaires pour gérer les moyens de paiement multiples

import {
  PaymentSplit,
  PrestationPaymentMethod,
  Prestation,
  PAYMENT_METHOD_LABELS,
} from '../types/entities';
import { ProductSale } from '../types/inventory';

/**
 * Tolérance pour la validation des montants (arrondis)
 */
const PAYMENT_TOLERANCE = 0.01;

/**
 * Convertit un paymentMethod unique en array de PaymentSplit (pour rétrocompatibilité)
 * @param paymentMethod - Le moyen de paiement unique
 * @param amount - Le montant total
 * @returns Array avec un seul PaymentSplit
 */
export function convertPaymentMethodToSplits(
  paymentMethod: PrestationPaymentMethod,
  amount: number
): PaymentSplit[] {
  return [{ method: paymentMethod, amount }];
}

/**
 * Récupère les paymentSplits d'une prestation (avec fallback sur paymentMethod si paymentSplits n'existe pas)
 * @param prestation - La prestation
 * @returns Array de PaymentSplit
 */
export function getPrestationPaymentSplits(prestation: Prestation): PaymentSplit[] {
  if (prestation.paymentSplits && prestation.paymentSplits.length > 0) {
    return prestation.paymentSplits;
  }
  // Fallback sur l'ancien format
  return convertPaymentMethodToSplits(prestation.paymentMethod, prestation.finalPrice);
}

/**
 * Récupère les paymentSplits d'une vente produit (avec fallback sur paymentMethod si paymentSplits n'existe pas)
 * @param sale - La vente produit
 * @returns Array de PaymentSplit
 */
export function getProductSalePaymentSplits(sale: ProductSale): PaymentSplit[] {
  if (sale.paymentSplits && sale.paymentSplits.length > 0) {
    return sale.paymentSplits;
  }
  // Fallback sur l'ancien format
  const amount = sale.amountPaid ?? sale.finalPrice ?? sale.totalPrice;
  return convertPaymentMethodToSplits(sale.paymentMethod, amount);
}

/**
 * Calcule le total des montants dans un array de PaymentSplit
 * @param splits - Array de PaymentSplit
 * @returns Somme des montants
 */
export function calculatePaymentSplitsTotal(splits: PaymentSplit[]): number {
  return splits.reduce((sum, split) => sum + split.amount, 0);
}

/**
 * Valide que la somme des paymentSplits correspond au montant attendu
 * @param splits - Array de PaymentSplit
 * @param expectedAmount - Montant attendu
 * @param tolerance - Tolérance pour les arrondis (défaut: 0.01)
 * @returns true si la somme correspond (dans la tolérance)
 */
export function validatePaymentSplits(
  splits: PaymentSplit[],
  expectedAmount: number,
  tolerance: number = PAYMENT_TOLERANCE
): boolean {
  if (splits.length === 0) {
    return false;
  }

  const total = calculatePaymentSplitsTotal(splits);
  const difference = Math.abs(total - expectedAmount);
  return difference <= tolerance;
}

/**
 * Valide que tous les montants dans les splits sont positifs
 * @param splits - Array de PaymentSplit
 * @returns true si tous les montants sont > 0
 */
export function validatePaymentSplitsAmounts(splits: PaymentSplit[]): boolean {
  return splits.every((split) => split.amount > 0);
}

/**
 * Valide que tous les moyens de paiement sont valides
 * @param splits - Array de PaymentSplit
 * @param validMethods - Array des moyens de paiement valides
 * @returns true si tous les moyens sont valides
 */
export function validatePaymentSplitsMethods(
  splits: PaymentSplit[],
  validMethods: PrestationPaymentMethod[] = ['espece', 'orange_money', 'mtn_mobile_money']
): boolean {
  return splits.every((split) => validMethods.includes(split.method));
}

/**
 * Valide complètement un array de PaymentSplit (montants FCFA arrondis).
 * Les lignes à montant 0 sont ignorées (ligne en cours de saisie).
 */
export type PaymentSplitValidationIssue = {
  key: string;
  params?: Record<string, string | number>;
};

export function validatePaymentSplitsComplete(
  splits: PaymentSplit[],
  expectedAmount: number
): { isValid: boolean; errors: string[]; issues: PaymentSplitValidationIssue[] } {
  const issues: PaymentSplitValidationIssue[] = [];
  const expected = Math.round(Number(expectedAmount) || 0);
  const active = (splits || []).filter((s) => Math.round(Number(s.amount) || 0) > 0);

  // Panier offert (total 0) : aucun encaissement requis.
  if (expected === 0) {
    if (active.length > 0) {
      const total = Math.round(calculatePaymentSplitsTotal(active));
      if (Math.abs(total - expected) > PAYMENT_TOLERANCE) {
        issues.push({
          key: 'paymentSplits.errors.sumMismatch',
          params: { total, expected },
        });
      }
    }
    const hasNegative = (splits || []).some((s) => Number(s.amount) < 0);
    if (hasNegative) {
      issues.push({ key: 'paymentSplits.errors.amountsPositive' });
    }
    const errors = issues.map((i) => i.key);
    return { isValid: issues.length === 0, errors, issues };
  }

  if (active.length === 0) {
    issues.push({ key: 'paymentSplits.errors.required' });
  }

  const hasNegative = (splits || []).some((s) => Number(s.amount) < 0);
  if (hasNegative) {
    issues.push({ key: 'paymentSplits.errors.amountsPositive' });
  }

  if (active.length > 0 && !validatePaymentSplitsMethods(active)) {
    issues.push({ key: 'paymentSplits.errors.methodsInvalid' });
  }

  if (active.length > 0) {
    const total = Math.round(calculatePaymentSplitsTotal(active));
    if (Math.abs(total - expected) > PAYMENT_TOLERANCE) {
      issues.push({
        key: 'paymentSplits.errors.sumMismatch',
        params: { total, expected },
      });
    }
  }

  // `errors` : libellés techniques pour tests legacy (clés i18n)
  const errors = issues.map((i) => i.key);

  return {
    isValid: issues.length === 0,
    errors,
    issues,
  };
}

type SplitMergeBucket = { amount: number; attachments: string[] };

/**
 * Répartit les `paymentSplits` du panier sur une ligne (prestation / vente produit).
 * Sans cela, chaque ligne recevait le montant total du panier → les rapports additionnaient N fois le même paiement.
 */
export function allocatePaymentSplitsForLineItem(
  cartSplits: PaymentSplit[] | undefined,
  lineAmountTtc: number,
  cartTotalTtc: number
): PaymentSplit[] | undefined {
  if (!cartSplits?.length) return undefined;
  if (!(lineAmountTtc > 0) || !(cartTotalTtc > 0)) return undefined;

  const merged = mergePaymentSplitsByMethod(cartSplits);
  const splitTotal = calculatePaymentSplitsTotal(merged);
  const denominator = splitTotal > PAYMENT_TOLERANCE ? splitTotal : cartTotalTtc;

  const targetCents = Math.round(lineAmountTtc * 100);
  if (targetCents <= 0) return undefined;

  let bestIdx = 0;
  merged.forEach((s, i) => {
    if (s.amount > merged[bestIdx].amount) bestIdx = i;
  });

  const amountCents = merged.map((s) => {
    const raw = (s.amount * lineAmountTtc) / denominator;
    return Math.max(0, Math.round(raw * 100));
  });

  let sumCents = amountCents.reduce((a, c) => a + c, 0);
  const drift = targetCents - sumCents;
  if (drift !== 0 && amountCents.length > 0) {
    amountCents[bestIdx] = Math.max(0, amountCents[bestIdx] + drift);
  }

  const out: PaymentSplit[] = merged
    .map((s, i) => ({
      method: s.method,
      amount: Math.round(amountCents[i]) / 100,
      ...(s.attachments?.length ? { attachments: s.attachments } : {}),
    }))
    .filter((s) => s.amount > PAYMENT_TOLERANCE);

  return out.length ? out : undefined;
}

/**
 * Pour une transaction groupée (plusieurs prestations / produits) : fusionne les paymentSplits
 * explicites de chaque ligne afin de ne pas perdre les justificatifs (souvent sur une seule ligne).
 * Si aucune ligne n'a de paymentSplits en base, retombe sur la 1ère prestation puis 1ère vente.
 *
 * Si des lignes dupliquaient encore le panier entier (bug historique), la somme fusionnée peut
 * dépasser le total TTC : corriger les documents via la migration Super Admin sur le business.
 */
export function getMergedPaymentSplitsFromTransactionItems(
  prestations: Prestation[],
  productSales: ProductSale[]
): PaymentSplit[] {
  const explicit: PaymentSplit[] = [];
  for (const p of prestations) {
    if (p.paymentSplits && p.paymentSplits.length > 0) {
      explicit.push(...p.paymentSplits);
    }
  }
  for (const s of productSales) {
    if (s.paymentSplits && s.paymentSplits.length > 0) {
      explicit.push(...s.paymentSplits);
    }
  }
  if (explicit.length > 0) {
    return mergePaymentSplitsByMethod(explicit);
  }
  if (prestations.length > 0) {
    return getPrestationPaymentSplits(prestations[0]);
  }
  if (productSales.length > 0) {
    return getProductSalePaymentSplits(productSales[0]);
  }
  return [];
}

/**
 * Répartit les splits pour que la somme égale exactement `cashIn` (encaissé).
 * Les montants splits peuvent être le panier facturé / intention initiale — on scale.
 * Si cashIn ≤ 0 → [].
 */
export function scalePaymentSplitsToCashIn(
  splits: Array<{ method: PrestationPaymentMethod; amount: number }>,
  cashIn: number
): Array<{ method: PrestationPaymentMethod; amount: number }> {
  const target = Math.max(0, Math.round(Number(cashIn) || 0));
  if (target <= 0) return [];

  const positive = splits.filter((s) => (Number(s.amount) || 0) > 0);
  if (positive.length === 0) return [];

  const sum = positive.reduce((acc, s) => acc + (Number(s.amount) || 0), 0);
  if (sum <= 0) return [];

  if (Math.abs(sum - target) < 0.5) {
    return positive.map((s) => ({
      method: s.method,
      amount: Math.round(Number(s.amount) || 0),
    }));
  }

  const scaled = positive.map((s) => ({
    method: s.method,
    amount: Math.round(((Number(s.amount) || 0) / sum) * target),
  }));
  const scaledSum = scaled.reduce((acc, s) => acc + s.amount, 0);
  const drift = target - scaledSum;
  if (drift !== 0 && scaled.length > 0) {
    let maxIdx = 0;
    for (let i = 1; i < scaled.length; i += 1) {
      if (scaled[i].amount > scaled[maxIdx].amount) maxIdx = i;
    }
    scaled[maxIdx] = {
      ...scaled[maxIdx],
      amount: scaled[maxIdx].amount + drift,
    };
  }
  return scaled;
}

/**
 * Fusionne les PaymentSplit qui ont le même moyen de paiement
 * (somme des montants + union des pièces jointes par méthode)
 */
export function mergePaymentSplitsByMethod(splits: PaymentSplit[]): PaymentSplit[] {
  const merged: Partial<Record<PrestationPaymentMethod, SplitMergeBucket>> = {};

  splits.forEach((split) => {
    const atts = (split.attachments ?? []).filter(Boolean);
    const prev = merged[split.method];
    if (prev) {
      prev.amount += split.amount;
      prev.attachments = Array.from(new Set([...prev.attachments, ...atts]));
    } else {
      merged[split.method] = {
        amount: split.amount,
        attachments: [...atts],
      };
    }
  });

  return (Object.entries(merged) as [PrestationPaymentMethod, SplitMergeBucket][]).map(
    ([method, data]) => ({
      method,
      amount: data.amount,
      ...(data.attachments.length > 0 ? { attachments: data.attachments } : {}),
    })
  );
}

/**
 * Splits non-espèces avec montant > 0 mais sans aucun justificatif (validation caisse).
 */
export function getNonCashSplitsMissingProof(splits: PaymentSplit[]): PaymentSplit[] {
  return splits.filter(
    (s) => s.method !== 'espece' && s.amount > 0 && (!s.attachments || s.attachments.length === 0)
  );
}

export interface PaymentProofEntry {
  method: PrestationPaymentMethod;
  label: string;
  urls: string[];
}

/** Justificatifs groupés par moyen de paiement (pour affichage rapports / fiche). */
export function getPaymentProofEntries(splits?: PaymentSplit[]): PaymentProofEntry[] {
  if (!splits?.length) return [];
  return splits
    .filter((s) => s.attachments && s.attachments.length > 0)
    .map((s) => ({
      method: s.method,
      label: PAYMENT_METHOD_LABELS[s.method],
      urls: s.attachments as string[],
    }));
}

export function transactionHasPaymentProofs(splits?: PaymentSplit[]): boolean {
  return getPaymentProofEntries(splits).length > 0;
}

/**
 * Obtient le moyen de paiement principal (celui avec le montant le plus élevé)
 * Utile pour la rétrocompatibilité avec paymentMethod
 * @param splits - Array de PaymentSplit
 * @returns Le moyen de paiement principal
 */
export function getPrimaryPaymentMethod(splits: PaymentSplit[]): PrestationPaymentMethod | null {
  if (splits.length === 0) {
    return null;
  }

  const sorted = [...splits].sort((a, b) => b.amount - a.amount);
  return sorted[0].method;
}

/**
 * Formate les paymentSplits pour l'affichage
 * Format : Espèce : 10000 FCFA | Orange Money : 100 FCFA | MTN Mobile Money : 900 FCFA
 * @param splits - Array de PaymentSplit
 * @param _totalAmount - Montant total (non utilisé, conservé pour rétrocompatibilité)
 * @returns String formatée
 */
export function formatPaymentSplitsForDisplay(
  splits: PaymentSplit[],
  _totalAmount?: number
): string {
  if (splits.length === 0) {
    return 'Aucun moyen de paiement';
  }

  return splits
    .map((split) => {
      const label = PAYMENT_METHOD_LABELS[split.method] ?? split.method;
      return `${label} : ${split.amount.toFixed(0)} FCFA`;
    })
    .join(' | ');
}
